/* -------------------------------------------------------
   ConversationSidebar — Chat history panel.
   Lists past conversations grouped by recency, with
   inline rename and delete-with-confirmation.
   ------------------------------------------------------- */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import type { ConversationSummary } from '../../types/chatV2';

/* ---------- date-grouping helper ---------- */

interface Group {
  label: string;
  items: ConversationSummary[];
}

function groupByDate(conversations: ConversationSummary[]): Group[] {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const oneDay = 86_400_000;

  const buckets: Group[] = [
    { label: 'Today', items: [] },
    { label: 'Yesterday', items: [] },
    { label: 'Previous 7 days', items: [] },
    { label: 'Previous 30 days', items: [] },
    { label: 'Older', items: [] },
  ];

  for (const c of conversations) {
    const t = new Date(c.lastMessageAt).getTime();
    if (t >= startOfToday) buckets[0].items.push(c);
    else if (t >= startOfToday - oneDay) buckets[1].items.push(c);
    else if (t >= startOfToday - 7 * oneDay) buckets[2].items.push(c);
    else if (t >= startOfToday - 30 * oneDay) buckets[3].items.push(c);
    else buckets[4].items.push(c);
  }

  return buckets.filter((b) => b.items.length > 0);
}

function displayTitle(c: ConversationSummary): string {
  return c.title || `Conversation`;
}

/* ---------- Sidebar item ---------- */

const SidebarItem: React.FC<{
  conv: ConversationSummary;
  isActive: boolean;
  onSelect: (id: string) => void;
  onRename: (id: string, title: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}> = ({ conv, isActive, onSelect, onRename, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditValue(displayTitle(conv));
    setEditing(true);
  };

  const commitRename = async () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== displayTitle(conv)) {
      try {
        await onRename(conv.id, trimmed);
      } catch {
        /* toast could go here */
      }
    }
    setEditing(false);
  };

  const startDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirming(true);
  };

  const confirmDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await onDelete(conv.id);
    } catch {
      /* toast */
    }
    setConfirming(false);
  };

  const cancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirming(false);
  };

  if (confirming) {
    return (
      <div className="cv2-sb-item cv2-sb-item--confirm">
        <span className="cv2-sb-item-warn">Delete this conversation?</span>
        <div className="cv2-sb-item-confirm-actions">
          <button
            className="cv2-sb-confirm-yes"
            onClick={confirmDelete}
            aria-label="Confirm delete"
          >
            Delete
          </button>
          <button
            className="cv2-sb-confirm-no"
            onClick={cancelDelete}
            aria-label="Cancel delete"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      className={`cv2-sb-item${isActive ? ' cv2-sb-item--active' : ''}`}
      onClick={() => !editing && onSelect(conv.id)}
      aria-label={displayTitle(conv)}
      aria-current={isActive ? 'true' : undefined}
    >
      {editing ? (
        <input
          ref={inputRef}
          className="cv2-sb-rename-input"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') setEditing(false);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label="Rename conversation"
        />
      ) : (
        <span className="cv2-sb-item-title">{displayTitle(conv)}</span>
      )}

      {!editing && (
        <span className="cv2-sb-item-actions">
          <span
            className="cv2-sb-action"
            role="button"
            tabIndex={0}
            title="Rename"
            onClick={startRename}
            onKeyDown={(e) =>
              e.key === 'Enter' && startRename(e as unknown as React.MouseEvent)
            }
            aria-label="Rename conversation"
          >
            &#9998;
          </span>
          <span
            className="cv2-sb-action cv2-sb-action--danger"
            role="button"
            tabIndex={0}
            title="Delete"
            onClick={startDelete}
            onKeyDown={(e) =>
              e.key === 'Enter' && startDelete(e as unknown as React.MouseEvent)
            }
            aria-label="Delete conversation"
          >
            &#128465;
          </span>
        </span>
      )}
    </button>
  );
};

/* ---------- Main sidebar ---------- */

interface Props {
  conversations: ConversationSummary[];
  activeId: string | null;
  isLoading: boolean;
  open: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onRename: (id: string, title: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

const ConversationSidebar: React.FC<Props> = ({
  conversations,
  activeId,
  isLoading,
  open,
  onClose,
  onSelect,
  onNewChat,
  onRename,
  onDelete,
}) => {
  const groups = groupByDate(conversations);

  const handleSelect = useCallback(
    (id: string) => {
      onSelect(id);
      onClose(); // close sidebar on mobile after selection
    },
    [onSelect, onClose],
  );

  const handleNew = useCallback(() => {
    onNewChat();
    onClose();
  }, [onNewChat, onClose]);

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="cv2-sb-backdrop"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`cv2-sidebar${open ? ' cv2-sidebar--open' : ''}`}
        aria-label="Conversation history"
      >
        {/* Header */}
        <div className="cv2-sb-header">
          <button
            className="cv2-sb-new-btn"
            onClick={handleNew}
            aria-label="New conversation"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Chat
          </button>
          {/* Close button (mobile) */}
          <button
            className="cv2-sb-close"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            &times;
          </button>
        </div>

        {/* Conversation list */}
        <div className="cv2-sb-list">
          {isLoading && conversations.length === 0 && (
            <div className="cv2-sb-empty">Loading&hellip;</div>
          )}

          {!isLoading && conversations.length === 0 && (
            <div className="cv2-sb-empty">
              No conversations yet.
              <br />
              Start one above!
            </div>
          )}

          {groups.map((g) => (
            <div key={g.label} className="cv2-sb-group">
              <div className="cv2-sb-group-label">{g.label}</div>
              {g.items.map((c) => (
                <SidebarItem
                  key={c.id}
                  conv={c}
                  isActive={c.id === activeId}
                  onSelect={handleSelect}
                  onRename={onRename}
                  onDelete={onDelete}
                />
              ))}
            </div>
          ))}
        </div>
      </aside>
    </>
  );
};

export default React.memo(ConversationSidebar);
